/**
 * Custel API client (lightweight fetch wrapper).
 *
 * - Cookie tabanlı auth (`credentials: 'include'`).
 * - 401'de otomatik refresh dener.
 * - Sprint 2'de OpenAPI'den üretilen tip-güvenli client'a geçilebilir.
 */

import { getImpersonationToken } from './impersonation';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

async function request<T = unknown>(path: string, opts: RequestOptions = {}, retried = false): Promise<T> {
  const method = (opts.method ?? 'GET').toUpperCase();
  const impToken = getImpersonationToken();

  // Sprint 34 — impersonation salt-okunur: yazma metodlarını client'ta da blokla.
  // (Backend ImpersonationReadOnlyGuard zaten 403 döner; bu, isteğin hiç gitmemesini
  //  ve tutarlı bir mesaj gösterilmesini sağlar.)
  if (impToken && method !== 'GET' && method !== 'HEAD') {
    throw new ApiError(403, { message: 'İmpersonation modunda yazma kapalı' }, 'İmpersonation modunda yazma kapalı');
  }

  // Multipart (FormData) desteği: gövdeyi JSON.stringify ETME + Content-Type'ı tarayıcıya
  // bırak (boundary'yi kendi ekler). Kart OCR görüntü yüklemesi (POST /card-stock/ocr-scan).
  const isFormData = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  const init: RequestInit = {
    ...opts,
    credentials: 'include',
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      Accept: 'application/json',
      // İmpersonation aktifse ephemeral token'ı bearer olarak gönder.
      ...(impToken ? { Authorization: `Bearer ${impToken}` } : {}),
      ...(opts.headers ?? {}),
    },
    body: opts.body === undefined ? undefined : isFormData ? (opts.body as FormData) : JSON.stringify(opts.body),
  };

  const response = await fetch(`${API_BASE}${path}`, init);

  // 401 + ilk deneme: refresh dene (impersonation modunda refresh YOK — token expire
  // olunca oturum biter; banner geri sayımı kullanıcıyı yönlendirir).
  if (response.status === 401 && !retried && !impToken && !path.startsWith('/auth/')) {
    const refreshOk = await tryRefresh();
    if (refreshOk) {
      return request<T>(path, opts, true);
    }
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const data = contentType.includes('application/json') ? await response.json() : await response.text();

  // F6 — zorunlu telefon doğrulama gate'i: backend 403 { code: 'PHONE_VERIFICATION_REQUIRED' }
  // dönerse kullanıcıyı zorunlu doğrulama ekranına yönlendir (zaten oradaysak/auth'taysak değil).
  if (
    response.status === 403 &&
    typeof data === 'object' &&
    data &&
    (data as { code?: string }).code === 'PHONE_VERIFICATION_REQUIRED' &&
    typeof window !== 'undefined' &&
    !window.location.pathname.startsWith('/telefon-dogrula')
  ) {
    window.location.assign('/telefon-dogrula');
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      data,
      typeof data === 'object' && data && 'message' in data
        ? String((data as { message: unknown }).message)
        : response.statusText,
    );
  }

  return data as T;
}

async function tryRefresh(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    return response.ok;
  } catch {
    return false;
  }
}

export const api = {
  get: <T = unknown>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'GET' }),
  post: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  patch: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  put: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  delete: <T = unknown>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'DELETE' }),
};
